/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/pages/Auth/OnboardingWizard.tsx
 * DESCRIPTION: Multi-step onboarding wizard with verified plan activation
 *              and lead-capture escape hatches. Slate-themed to match
 *              product brand identity.
 *
 * FLOW:
 * Step 1: Industry selection (tailors AI assessments).
 * Step 2: Plan activation via Clerk Billing checkout with AUTOMATIC
 *         subscription detection + "Request a demo" lead capture.
 * Step 3: Success panel with auto-redirect (celebration screen).
 *
 * FEATURES:
 * - SLATE THEME: Professional dark/slate gradient replaces blue marketing
 *   gradient, matching product identity throughout the app
 * - AUTO-DETECTION: watches live Clerk subscription (experimental hook);
 *   the moment checkout completes, shows success panel + toast + redirect
 * - VERIFICATION, NOT DECLARATION: access granted by the gate only when
 *   a real non-default subscription exists (App.tsx v2.4.0)
 * - CANCEL (not sign-out): dismisses the wizard, sets preview_started_at
 *   metadata flag, lands user in the app shell with limited access
 * - NO DEAD ENDS: "Request a demo" lead capture, Cancel escape hatch
 * - NO FREEMIUM: vouchers are Clerk coupons redeemed at checkout
 * - Toast notifications (success/error) via ToastContext
 * - Industry locking (30-day rule, admin/enterprise/business override)
 * - Full dark mode support and mobile responsiveness
 *
 * METADATA CONTRACT (unsafeMetadata):
 * - Step 1: industry, industry_set_at, onboarding_completed
 * - Cancel: preview_started_at (opens Explorer tier in ProtectedRoute)
 * - Demo request: demo_requested_at, demo_company, demo_note
 * - On verified success: plan_id, plan_confirmed_at
 *
 * RISK NOTE: uses @clerk/react/experimental (useSubscription).
 * Pin @clerk/react; re-validate shapes via [planAccess] dev log on upgrades.
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 1.8.0
 * @created 2026-08-25
 * @updated 2026-09-25 - v1.8.0: SLATE THEME replaces blue gradient;
 *                       "Cancel" button replaces "Sign out" — dismisses
 *                       wizard, sets preview_started_at, lands user in
 *                       Explorer tier (app shell with limited access);
 *                       eliminates dead ends while preserving conversion
 *                       paths via Request a demo lead capture
 * ─────────────────────────────────────────────────────────────────────
 */
import { useState, useEffect, useRef } from 'react'
import { useUser, PricingTable } from '@clerk/react'
import { useSubscription } from '@clerk/react/experimental'
import {
  CheckCircle,
  ArrowRight,
  Lock,
  AlertCircle,
  CreditCard,
  PartyPopper,
  Send,
  X,
} from 'lucide-react'
import { normalizeSubscription, unwrapSubscriptionHook } from '../../lib/planAccess'
import { useToast } from '../../contexts/ToastContext'

// Industry sectors matching backend API prompts
const INDUSTRY_SECTORS = [
  { id: 'commercial_product', label: 'Commercial / Product Image' },
  { id: 'defense_intelligence', label: 'Defense & Intelligence' },
  { id: 'financial_services', label: 'Financial Institutions / KYC-AML' },
  { id: 'government_public', label: 'Government & Public Sector' },
  { id: 'human_resources', label: 'Human Resources / KYE' },
  { id: 'identity_document', label: 'Identity / KYC Document' },
  { id: 'legal_ediscovery', label: 'Legal & E-Discovery' },
  { id: 'news_media', label: 'News & Media Editorial' },
  { id: 'platforms_marketplaces', label: 'Platforms & Marketplaces' },
  { id: 'political_content', label: 'Political Campaigns & Messaging' },
  { id: '', label: 'Generic Forensic Analysis' },
  { id: 'dating_profile', label: 'Dating Profile Picture' },
  { id: 'artistic_creative', label: 'Artistic / Creative Work' },
] as const

type IndustrySector = typeof INDUSTRY_SECTORS[number]['id']

interface OnboardingWizardProps {
  onComplete: () => void
  redirectPath?: string
}

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ onComplete }) => {
  const { user } = useUser()
  const toast = useToast()
  const subHook: any = useSubscription()
  const { subscription } = unwrapSubscriptionHook(subHook)
  const norm = normalizeSubscription(subscription)

  // Flow state
  const [step, setStep] = useState<'industry' | 'plan' | 'success'>('industry')
  const [selectedIndustry, setSelectedIndustry] = useState<IndustrySector>('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const successHandled = useRef(false)

  // Demo request state (lead capture)
  const [showDemoForm, setShowDemoForm] = useState(false)
  const [demoCompany, setDemoCompany] = useState('')
  const [demoNote, setDemoNote] = useState('')
  const [isSendingDemo, setIsSendingDemo] = useState(false)
  const [demoSent, setDemoSent] = useState(false)

  // Industry locking state
  const [isLocked, setIsLocked] = useState(false)
  const [daysRemaining, setDaysRemaining] = useState(0)

  useEffect(() => {
    if (user) {
      checkLockStatus()
      const metadata = user.unsafeMetadata as any
      if (metadata?.industry) {
        setSelectedIndustry(metadata.industry)
      }
      // Returning users who finished industry step go straight to plans
      if (metadata?.onboarding_completed && step === 'industry') {
        setStep('plan')
      }
      // Returning users with a pending demo request see the confirmation
      if (metadata?.demo_requested_at) {
        setDemoSent(true)
      }
    }
  }, [user])

  /**
   * AUTO-DETECTION: the moment a real paid subscription appears,
   * celebrate and redirect.
   */
  useEffect(() => {
    if (step === 'plan' && norm.isPaid && !successHandled.current) {
      successHandled.current = true
      toast.success(`Subscription active — ${norm.planName || 'your plan'} is ready. Welcome aboard!`)
      if (user) {
        user
          .update({
            unsafeMetadata: {
              ...user.unsafeMetadata,
              plan_id: norm.planId || 'paid_checkout',
              plan_confirmed_at: Date.now(),
              onboarding_completed: true,
            },
          })
          .catch(() => {
            toast.info('Access granted. A profile sync will retry later.')
          })
      }
      setStep('success')
      window.setTimeout(() => onComplete(), 2600)
    }
  }, [step, norm.isPaid, norm.planName, norm.planId, user, toast, onComplete])

  const checkLockStatus = () => {
    if (!user) return
    const metadata = user.unsafeMetadata as any
    if (!metadata?.industry_set_at) {
      setIsLocked(false)
      return
    }
    const license = metadata?.license
    const role = metadata?.role
    if (role === 'admin' || license === 'enterprise' || license === 'business') {
      setIsLocked(false)
      return
    }
    const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000
    const timeSinceSet = Date.now() - metadata.industry_set_at
    if (timeSinceSet < thirtyDaysInMs) {
      setIsLocked(true)
      const remainingMs = thirtyDaysInMs - timeSinceSet
      setDaysRemaining(Math.ceil(remainingMs / (1000 * 60 * 60 * 24)))
    } else {
      setIsLocked(false)
    }
  }

  /**
   * Step 1 completion: save industry, advance to plan selection.
   */
  const handleComplete = async () => {
    if (!user || !selectedIndustry) return
    setIsSaving(true)
    setError('')
    try {
      const metadata = user.unsafeMetadata as any
      if (!isLocked) {
        await user.update({
          unsafeMetadata: {
            ...user.unsafeMetadata,
            industry: selectedIndustry,
            onboarding_completed: true,
            industry_set_at: metadata?.industry_set_at || Date.now(),
          },
        })
      }
      setStep('plan')
    } catch (err: any) {
      toast.error(err.message || 'Failed to save preferences')
      setError(err.message || 'Failed to save preferences')
    } finally {
      setIsSaving(false)
    }
  }

  /**
   * Fallback: user completed checkout but detection hasn't fired.
   * Reload re-fetches subscription and re-runs the gate.
   */
  const handleVerify = () => {
    toast.info('Verifying your checkout…')
    window.location.reload()
  }

  /**
   * CANCEL (not sign-out): dismiss the wizard and enter the app shell
   * as an Explorer (preview tier). User can still sign out later from
   * the sidebar/header. Sets preview_started_at so the gate lets them
   * through with limited access.
   */
  const handleCancel = async () => {
    if (!user) return
    setIsSaving(true)
    try {
      // Default to generic forensic if no industry picked yet
      const industryToUse = selectedIndustry || ''
      await user.update({
        unsafeMetadata: {
          ...user.unsafeMetadata,
          industry: user.unsafeMetadata?.industry || industryToUse,
          preview_started_at: Date.now(),
        },
      })
      toast.info('You can explore in preview mode. Upgrade anytime to unlock full features.')
      onComplete()
    } catch (err: any) {
      toast.error('Could not enter preview mode')
    } finally {
      setIsSaving(false)
    }
  }

  /**
   * Lead capture: record a demo request on the user's profile.
   */
  const handleDemoSubmit = async () => {
    if (!user) return
    setIsSendingDemo(true)
    setError('')
    try {
      await user.update({
        unsafeMetadata: {
          ...user.unsafeMetadata,
          demo_requested_at: Date.now(),
          demo_company: demoCompany.trim() || null,
          demo_note: demoNote.trim() || null,
        },
      })
      setDemoSent(true)
      setShowDemoForm(false)
      toast.success('Demo request received — our team will contact you shortly.')
    } catch (err: any) {
      toast.error(err.message || 'Could not send your demo request')
      setError(err.message || 'Could not send your demo request')
    } finally {
      setIsSendingDemo(false)
    }
  }

  const userEmail = user?.primaryEmailAddress?.emailAddress || ''

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-6">
      <div
        className={`bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full overflow-hidden border border-gray-200 dark:border-gray-700 flex flex-col max-h-[90vh] ${
          step === 'plan' ? 'max-w-5xl' : 'max-w-3xl'
        }`}
      >
        {/* Header — SLATE THEME */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 dark:from-slate-700 dark:to-slate-800 p-6 sm:p-8 text-white shrink-0">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl sm:text-3xl font-bold">
              {step === 'industry'
                ? 'Welcome to Onboarding'
                : step === 'plan'
                  ? 'Activate your account'
                  : "You're all set!"}
            </h2>
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white/10 border border-white/20">
              Step {step === 'industry' ? 1 : step === 'plan' ? 2 : 3} of 2
            </span>
          </div>
          <p className="text-slate-200 text-sm sm:text-base">
            {step === 'industry'
              ? 'Select your primary industry to tailor your AI assessments and analysis models.'
              : step === 'plan'
                ? 'Activate with a paid plan, redeem a voucher at checkout, or explore in preview mode.'
                : 'Your subscription is active. Taking you to your workspace…'}
          </p>
        </div>

        {/* Content */}
        <div className="p-6 sm:p-8 overflow-y-auto flex-grow">
          {error && step !== 'success' && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {step === 'industry' && (
            <>
              {isLocked && (
                <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                  <Lock className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Industry selection is currently locked.</p>
                    <p className="text-xs mt-1 opacity-90">
                      Changes are restricted for 30 days after initial selection. You have {daysRemaining} days remaining.
                    </p>
                  </div>
                </div>
              )}
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                    Which industry best describes your work?
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {INDUSTRY_SECTORS.map((sector) => (
                      <button
                        type="button"
                        key={sector.id}
                        onClick={() => !isLocked && setSelectedIndustry(sector.id)}
                        disabled={isLocked}
                        className={`p-4 rounded-xl border text-left transition-all relative ${
                          selectedIndustry === sector.id
                            ? 'border-slate-500 bg-slate-50 dark:bg-slate-900/40 ring-1 ring-slate-500'
                            : 'border-gray-200 dark:border-gray-700 hover:border-slate-400 dark:hover:border-slate-500 bg-white dark:bg-gray-700/50'
                        } ${isLocked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'}`}
                      >
                        <div className="flex items-center justify-between">
                          <span
                            className={`font-medium text-sm sm:text-base ${
                              selectedIndustry === sector.id
                                ? 'text-slate-800 dark:text-slate-200'
                                : 'text-gray-900 dark:text-white'
                            }`}
                          >
                            {sector.label}
                          </span>
                          {selectedIndustry === sector.id && (
                            <CheckCircle className="w-5 h-5 text-slate-700 dark:text-slate-300 shrink-0" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {step === 'plan' && (
            <div className="space-y-6">
              {/* Voucher / demo guidance */}
              <div className="rounded-xl bg-slate-50 dark:bg-slate-900/30 border border-slate-200 dark:border-slate-700 p-4 text-sm text-slate-800 dark:text-slate-200">
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 shrink-0 mt-0.5 text-slate-600 dark:text-slate-400" />
                  <div>
                    <p className="font-medium">Choose how you want to use the Software.</p>
                    <p className="mt-1 text-xs opacity-90">
                      Have a voucher or promo code? Select any plan and enter your code in the
                      secure checkout — 100% vouchers grant full access. Not ready yet? Request
                      a demo or explore in preview mode below.
                    </p>
                  </div>
                </div>
              </div>

              {/* Clerk PricingTable in a light island */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
                <PricingTable
                  appearance={{
                    variables: {
                      colorPrimary: '#1e293b', // slate-800
                      colorText: '#111827',
                      colorTextSecondary: '#6b7280',
                      colorBackground: '#ffffff',
                      colorInputBackground: '#ffffff',
                      colorInputText: '#111827',
                      borderRadius: '0.75rem',
                    },
                  }}
                  checkoutProps={{
                    appearance: {
                      variables: {
                        colorPrimary: '#1e293b',
                        borderRadius: '0.75rem',
                      },
                    },
                  }}
                />
              </div>

              {/* Demo request */}
              {demoSent ? (
                <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-5">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-emerald-900 dark:text-emerald-300">
                        Demo request received
                      </p>
                      <p className="mt-1 text-sm text-emerald-800 dark:text-emerald-400">
                        Our team will reach out at{' '}
                        <span className="font-medium">{userEmail || 'your email'}</span> within one
                        business day.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                showDemoForm && (
                  <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-4 sm:p-5">
                    <p className="font-semibold text-gray-900 dark:text-white text-sm mb-1">
                      Request a personalized demo
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                      See on your own use case before subscribing.
                    </p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                          Work email
                        </label>
                        <input
                          type="email"
                          value={userEmail}
                          readOnly
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700/60 text-gray-700 dark:text-gray-200 text-sm cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                          Company <span className="text-gray-400">(optional)</span>
                        </label>
                        <input
                          type="text"
                          value={demoCompany}
                          onChange={(e) => setDemoCompany(e.target.value)}
                          placeholder="e.g. Acme Media Forensics"
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                          What would you like to see? <span className="text-gray-400">(optional)</span>
                        </label>
                        <textarea
                          value={demoNote}
                          onChange={(e) => setDemoNote(e.target.value)}
                          rows={3}
                          placeholder="e.g. KYC document verification at scale…"
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleDemoSubmit}
                        disabled={isSendingDemo}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors"
                      >
                        {isSendingDemo ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Sending…
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            Send demo request
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )
              )}
            </div>
          )}

          {step === 'success' && (
            <div className="py-10 text-center">
              <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-6">
                <PartyPopper className="w-10 h-10 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Thank you — your plan is active!
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {norm.planName ? `${norm.planName} • ` : ''}Subscription verified successfully.
              </p>
              <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <div className="w-4 h-4 border-2 border-slate-600 border-t-transparent rounded-full animate-spin" />
                Redirecting you to your workspace…
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions — Cancel (dismiss) + primary action */}
        {step !== 'success' && (
          <div className="p-6 sm:p-8 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 shrink-0 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
            {/* CANCEL: dismiss wizard, enter preview mode (Explorer tier) */}
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSaving}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
              {isSaving ? 'Entering preview…' : 'Explore in preview mode'}
            </button>

            <div className="flex flex-col sm:flex-row gap-3">
              {step === 'plan' && !demoSent && (
                <button
                  type="button"
                  onClick={() => setShowDemoForm(!showDemoForm)}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium rounded-lg transition-colors shadow-sm"
                >
                  <Send className="w-4 h-4" />
                  {showDemoForm ? 'Close demo request' : 'Request a demo'}
                </button>
              )}
              {step === 'industry' ? (
                <button
                  type="button"
                  onClick={handleComplete}
                  disabled={!selectedIndustry || isSaving}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors shadow-sm"
                >
                  {isSaving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Continue to plan selection
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleVerify}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white font-medium rounded-lg transition-colors shadow-sm"
                >
                  I've completed checkout — verify now
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default OnboardingWizard
